
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/use-user';
import { toast } from 'sonner';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useR2Upload } from '@/hooks/use-r2-upload';

// Validation schema
const campaignSchema = z.object({
  title: z.string().min(3, { message: "Título deve ter pelo menos 3 caracteres" }),
  description: z.string().min(10, { message: "Descrição deve ter pelo menos 10 caracteres" }),
  goal_amount: z.coerce.number().min(100, { message: "Meta mínima de R$ 100" }),
  deadline: z.coerce.date().min(new Date(), { message: "Data deve ser no futuro" }),
});

type CampaignFormData = z.infer<typeof campaignSchema>;

export default function CreateCampaign() {
  const { user, isLoading } = useUser();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [coverImageUrl, setCoverImageUrl] = useState<string>('');

  // Log user state for debugging
  useEffect(() => {
    console.log("CreateCampaign - User state:", { user, isLoading });
    
    // Only set authCheckComplete after isLoading is false
    if (!isLoading) {
      setAuthCheckComplete(true);
    }
  }, [user, isLoading]);

  // Check if user is logged in after loading is complete
  useEffect(() => {
    if (authCheckComplete && !user) {
      console.log("User not authenticated, redirecting to auth page");
      toast.error('Você precisa estar logado para criar uma campanha');
      navigate('/auth');
    }
  }, [authCheckComplete, user, navigate]);

  const { 
    previewUrl, 
    fileInputRef, 
    handleFileChange, 
    handleThumbnailClick,
    handleUpload
  } = useR2Upload({
    folder: 'campaign-covers',
    userId: user?.id,
  });

  const form = useForm<CampaignFormData>({
    resolver: zodResolver(campaignSchema),
    defaultValues: {
      title: '',
      description: '',
      goal_amount: undefined,
      deadline: undefined,
    }
  });

  const onSubmit = async (data: CampaignFormData) => {
    if (!user) {
      toast.error('Você precisa estar logado para criar uma campanha');
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload cover image if exists
      let imageUrl = '';
      if (fileInputRef.current?.files?.[0]) {
        try {
          // Use the handleUpload function from useR2Upload
          imageUrl = await handleUpload(fileInputRef.current.files[0]);
          console.log("Image uploaded successfully:", imageUrl);
        } catch (uploadError) {
          console.error("Error uploading image:", uploadError);
          toast.error('Erro ao fazer upload da imagem');
          setIsSubmitting(false);
          return;
        }
      }

      // Convert the Date object to an ISO string for Supabase
      const deadlineString = data.deadline.toISOString();

      // Insert campaign
      const { data: campaignData, error } = await supabase
        .from('campaigns')
        .insert({
          user_id: user.id,
          title: data.title,
          description: data.description,
          goal_amount: data.goal_amount,
          deadline: deadlineString,
          cover_image_url: imageUrl || null,
        })
        .select();

      if (error) throw error;

      toast.success('Campanha criada com sucesso!');
      navigate('/apoia');
    } catch (error) {
      console.error('Erro ao criar campanha:', error);
      toast.error('Não foi possível criar a campanha');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show a loading state while checking authentication
  if (isLoading || !authCheckComplete) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-2">Carregando...</span>
        </div>
      </div>
    );
  }

  // If authentication check is complete and user is null, we'll redirect (handled by useEffect)
  if (!user) {
    return null;
  }

  return (
    <div className="container mx-auto py-8">
      <h1 className="text-2xl font-bold mb-6">Criar Nova Campanha de Financiamento</h1>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Título da Campanha</FormLabel>
                <FormControl>
                  <Input placeholder="Digite o título da sua campanha" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descrição</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Conte mais sobre sua campanha e seus objetivos" 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="goal_amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Meta de Financiamento (R$)</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      placeholder="Quanto você precisa arrecadar?" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="deadline"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data Limite da Campanha</FormLabel>
                  <FormControl>
                    <Input 
                      type="date" 
                      {...field} 
                      value={field.value instanceof Date 
                        ? field.value.toISOString().split('T')[0] 
                        : field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div>
            <FormLabel>Imagem de Capa</FormLabel>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
              accept="image/*" 
              className="hidden" 
            />
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleThumbnailClick}
              className="mt-2"
            >
              {previewUrl ? 'Alterar Imagem' : 'Selecionar Imagem'}
            </Button>
            {previewUrl && (
              <img 
                src={previewUrl} 
                alt="Pré-visualização da capa" 
                className="mt-4 max-w-xs max-h-48 object-cover" 
              />
            )}
          </div>

          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? 'Criando Campanha...' : 'Criar Campanha'}
          </Button>
        </form>
      </Form>
    </div>
  );
}
